// The one Advance · Hold · Pass control of Steps 7 and 9 ("Retain Advance, Hold, and Pass. Require reason for Hold and
// Pass."): **Advance**, and a SplitButton each for **Hold** and **Pass** whose menu lists the configured reasons; the
// chosen decision then shows its reason (Hold: the Admin's hold reasons; Pass: the disposition reasons — "Pass cannot
// finish without disposition reason.") and the free reason the record keeps. Also the pinned résumé link: the Hiring
// Manager reads the version the slate carried, never a later upload (A-38).

import type { ApplicationCard } from "@/api/tools";
import { candidateOpen } from "@/api/tools";
import {
  ACTION_LABELS, Button, DOCUMENT_LABELS, Field, Flex, G1_LABELS, Pill, ReasonBox, SplitButton, Stack, TextArea, TextLink,
} from "@/mc";
import { useSettledRead } from "@/views/shared/settledRead";
import type { Choice } from "@/views/candidate/options";
import type { DecisionDraft } from "./pipeline";

export type DecisionScope = "decide" | "select" | `review.${number}`;

export interface DecisionControlsProps {
  draft: DecisionDraft;
  onChange: (draft: DecisionDraft) => void;
  /** The Hold reasons: the Admin's published `reasons:application_hold` (`useHoldReasonChoices`, read by the caller);
   *  Step 9's hold takes none: pass []. */
  holdReasons: readonly Choice[];
  /** The Pass reasons: the Admin's published `reasons:pass` (`usePassReasonChoices`, read by the caller). */
  passReasons: readonly Choice[];
  /** Where the controls sit, under the lane's `pipe` prefix: `decide` (the slide-over), `select` (Step 9) or
   *  `review.<n>` (a batch row); every key is `pipe.<scope>.<control>`, its static head the lane's own. */
  scope: DecisionScope;
  busy?: boolean | undefined;
}

export function DecisionControls({ draft, onChange, holdReasons, passReasons, scope, busy }: DecisionControlsProps) {
  const pick = (value: DecisionDraft["value"], reason = "") => onChange({ ...draft, value, reason });
  const reasons = draft.value === "hold" ? holdReasons : draft.value === "pass" ? passReasons : [];
  return (
    <Stack gap={8}>
      <Flex gap={8} wrap align="center">
        <Button sm tone={draft.value === "advance" ? "blue" : "ghost"} busy={busy} onClick={() => pick("advance")} >
          {ACTION_LABELS.advance}
        </Button>
        {holdReasons.length === 0 ? (
          <Button sm tone={draft.value === "hold" ? "blue" : "ghost"} busy={busy} onClick={() => pick("hold")} >{ACTION_LABELS.hold}</Button>
        ) : (
          <SplitButton
            sm
            label={ACTION_LABELS.hold}
            busy={busy}
            onClick={() => pick("hold")}
            reasons={holdReasons.map((choice) => ({ label: choice.label, onSelect: () => pick("hold", choice.value) }))}
            hs={{ main: `pipe.${scope}.hold`, menu: `pipe.${scope}.hold-reason` }}
          />
        )}
        <SplitButton
          sm
          label={ACTION_LABELS.pass}
          busy={busy}
          onClick={() => pick("pass")}
          reasons={passReasons.map((choice) => ({ label: choice.label, onSelect: () => pick("pass", choice.value) }))}
          hs={{ main: `pipe.${scope}.pass`, menu: `pipe.${scope}.pass-reason` }}
        />
        {draft.value ? <Pill tone={draft.value === "advance" ? "ok" : draft.value === "hold" ? "warn" : "neutral"}>{ACTION_LABELS[draft.value]}</Pill> : null}
      </Flex>
      {draft.value && reasons.length > 0 ? (
        <ReasonBox
          reasons={reasons}
          reason={draft.reason}
          onReason={(reason) => onChange({ ...draft, reason })}
          notes={draft.notes}
          onNotes={(notes) => onChange({ ...draft, notes })}
          hint={G1_LABELS.requireReason}
          hs={{ reason: `pipe.${scope}.reason`, notes: `pipe.${scope}.notes` }}
        />
      ) : draft.value ? (
        <Field label={G1_LABELS.rationale} required hint={G1_LABELS.reasonStored}>
          <TextArea rows={2} value={draft.notes} onChange={(event) => onChange({ ...draft, notes: event.target.value })}  />
        </Field>
      ) : null}
    </Stack>
  );
}

/** The résumé version pinned for this review, through the file door (the HM receives that version only). */
export function OriginalResume({ rid, card, hs }: { rid: string; card: Pick<ApplicationCard, "id">; hs: string }) {
  const open = useSettledRead(["application", card.id, rid], () => candidateOpen({ requisition_id: rid, application_id: card.id }));
  const fileId = open.data && "resume_file_id" in open.data ? open.data.resume_file_id : null;
  if (!fileId) return null;
  return <TextLink href={`/api/files/${encodeURIComponent(fileId)}`} icon="down" hs={hs}>{DOCUMENT_LABELS.originalResume}</TextLink>;
}
